import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import useToaster from "../../customHook/useToaster";
import { 
  addVendorServiceLocation, 
  getCitiesByStateVendor, 
  getStateListVendor,
  getSubProductsByLob 
} from "../../Services/vendorManagement.service";
import CommonModal from "../../CommonComponents/Modal/CommonModal";
import CustomSelect from "../../CommonComponents/Select/CustomSelect";
import CommonButton from "../../CommonComponents/CommonButton";
import CommonInput from "../../CommonComponents/CommonInput"; 
import styles from "./VendorManagement.module.css";

const FieldError = ({ error }) =>
  error ? <div className={styles["errorText"]}>{error}</div> : null;

const AddLocationModal = ({
  open,
  onClose,
  vendorId,
  employeeId,
  isMotorOnly,
  onSuccess,
}) => {
  const { showToast } = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  
  const [cityOptionsMap, setCityOptionsMap] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchedHealthSubProducts, setFetchedHealthSubProducts] = useState([]);

  const getEmptyRow = () => ({
    id: Date.now() + Math.random(),
    state: "",
    city: isMotorOnly ? [] : "",   
    sub_products: [],
    capacity: "",
  });

  const [locations, setLocations] = useState([getEmptyRow()]);

  useEffect(() => {
    if (open) {
      setLocations([getEmptyRow()]);
      setErrors({});
      setCityOptionsMap({});
      fetchStates();
      if (!isMotorOnly) {
        fetchHealthSubProducts();
      }
    }
  }, [open, isMotorOnly]);

  const fetchStates = async () => {
    try {
      const res = await getStateListVendor();
      if (res?.success || res?.data) {
        setStateOptions(res.data || res || []);
      }
    } catch (err) {
      showToast("Unable to load states.", "error", true);
    }
  };

  const fetchCities = async (stateId, rowId) => {
    try {
      const res = await getCitiesByStateVendor(stateId);
      if (res?.success || res?.data) {
        setCityOptionsMap((prev) => ({ ...prev, [rowId]: res.data || res || [] }));
      } else {
        setCityOptionsMap((prev) => ({ ...prev, [rowId]: [] }));
      }
    } catch (err) {
      showToast("Unable to load cities.", "error", true);
      setCityOptionsMap((prev) => ({ ...prev, [rowId]: [] }));
    }
  };

  const fetchHealthSubProducts = async () => {
    try {
      const res = await getSubProductsByLob(2);
      if (res?.success) {
        setFetchedHealthSubProducts(res.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch health sub products", err);
    }
  };

  const getAvailableStates = (currentRowId) => {
    const selectedStates = locations
      .filter((loc) => loc.id !== currentRowId)
      .map((loc) => loc.state);
      
    return stateOptions
      .map((opt) => opt.state_name)
      .filter((stateName) => !selectedStates.includes(stateName));
  };

  const handleStateChange = (id, e) => {
    const value = e.target.value;
    setErrors((prev) => ({ ...prev, [id]: { ...prev[id], state: null, city: null } }));
    setLocations((prev) => 
      prev.map((loc) => loc.id === id ? { ...loc, state: value, city: isMotorOnly ? [] : "" } : loc)
    );

    const selectedState = stateOptions.find(
      (opt) => opt.state_name?.toLowerCase() === value?.toLowerCase()
    );

    if (selectedState?.state_id) {
      fetchCities(selectedState.state_id, id);
    } else {
      setCityOptionsMap((prev) => ({ ...prev, [id]: [] }));
    }
  };

  const handleChange = (id, e) => {
    let { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [name]: null } }));
    
    setLocations((prev) => prev.map((loc) => {
      if (loc.id === id) {
        if (name === "city" && isMotorOnly) {
          value = typeof value === "string" ? value.split(",") : value;
        } else if (name === "sub_products") {
          value = typeof value === "string" ? value.split(",") : value;
        } else if (name === "capacity") {
          value = value.replace(/[^0-9]/g, "");
        }
        return { ...loc, [name]: value };
      }
      return loc;
    }));
  };

  const handleAddLocation = () => {
    setLocations([...locations, getEmptyRow()]);
  };

  const handleRemoveLocation = (idToRemove) => {
    setLocations((prev) => prev.filter((loc) => loc.id !== idToRemove));
    const newCityMap = { ...cityOptionsMap };
    delete newCityMap[idToRemove];
    setCityOptionsMap(newCityMap);
    const newErrors = { ...errors };
    delete newErrors[idToRemove];
    setErrors(newErrors);
  };

  const validate = () => {
    let isValid = true;
    const newErrors = {};
    locations.forEach((loc) => {
      const rowErrors = {};
      if (!loc.state) rowErrors.state = "State is required.";
      if (isMotorOnly ? (!loc.city || loc.city.length === 0) : !loc.city) {
        rowErrors.city = "City is required.";
      }
      if (!isMotorOnly) {
        if (!loc.sub_products || loc.sub_products.length === 0)
          rowErrors.sub_products = "Sub Product is required.";
        if (!loc.capacity) rowErrors.capacity = "Capacity is required.";
      }
      if (Object.keys(rowErrors).length > 0) {
        newErrors[loc.id] = rowErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) {
      showToast("Please enter correct details.", "error", true);
      return;
    }
    setIsSubmitting(true);
    const locationArr = locations.map((loc) => {
      const mappedSubProducts = loc.sub_products.map((name) => {
        const match = fetchedHealthSubProducts.find(sp => sp.sub_product_name === name);
        return match ? match.sub_product_id : null;
      }).filter(Boolean);
      return {
        state: loc.state,
        city: loc.city, 
        per_week_capacity: isMotorOnly ? 0 : Number(loc.capacity),
        sub_products: isMotorOnly ? [] : mappedSubProducts,
      };
    });

    const payload = {
      vendor_company_id: vendorId,
    //   employee_id: employeeId,
      location: locationArr,
    };

    try {
      const response = await addVendorServiceLocation(payload);
      if(response?.message.includes("All locations already exist")) {
        showToast("Location already exists for this vendor.", "error", true);
        return;
      }
      if (response?.success || response?.status === 200) {
        showToast("Location added successfully!", "success", true);
        onSuccess();
        onClose();
      } else {
        showToast(response?.message || "Failed to add location.", "error", true);
      }
    } catch (error) {
      showToast("Error adding location.", "error", true);
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommonModal 
      open={open} 
      onClose={onClose} 
      label="Add Service Location"
      width="600px" 
    >
      <Box className={styles["modalContentWrapper"]}>
        
        {locations.map((loc, index) => (
          <Box 
            key={loc.id} 
            className={styles["locationRowBox"]}
          >
            {locations.length > 1 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" className={styles["locationRowHeader"]}>
                <Typography className={styles["locationNumberText"]}>
                  Location #{index + 1}
                </Typography>
                <span 
                  onClick={() => handleRemoveLocation(loc.id)} 
                  className={styles["removeLocationText"]}
                >
                  Remove
                </span>
              </Stack>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  name="state"
                  label="State"
                  required={true}
                  customSelectValue={loc.state}
                  options={getAvailableStates(loc.id)} 
                  placeholder="Select State"
                  handleChange={(e) => handleStateChange(loc.id, e)}
                  height="3rem"
                  labelFont="1.2rem"
                />
                <FieldError error={errors[loc.id]?.state} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  name="city"
                  multiple={isMotorOnly} 
                  label="City"
                  required={true}
                  customSelectValue={loc.city}
                  options={(cityOptionsMap[loc.id] || []).map((opt) => opt.city_name || opt)} 
                  placeholder="Select City"
                  handleChange={(e) => handleChange(loc.id, e)}
                  disabled={!loc.state}
                  height="3rem"
                  labelFont="1.2rem"
                />
                <FieldError error={errors[loc.id]?.city} />
              </Grid>

              {!isMotorOnly && (
                <>
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      multiple={true}
                      name="sub_products"
                      label="Sub Products"
                      required={true}
                      customSelectValue={loc.sub_products}
                      options={fetchedHealthSubProducts.map(sp => sp.sub_product_name)} 
                      placeholder="Select Sub Products"
                      handleChange={(e) => handleChange(loc.id, e)}
                      height="3rem"
                      labelFont="1.2rem"
                    />
                    <FieldError error={errors[loc.id]?.sub_products} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box className={styles["capacityFieldWrapper"]}>
                      <Typography className={styles["capacityLabel"]}>
                        Capacity <span style={{ color: "red"}}>*</span>
                      </Typography>
                      <div className={styles["capacityInputWrapper"]}>
                        <CommonInput
                          name="capacity"
                          value={loc.capacity}
                          placeholder="Enter Weekly Capacity"
                          onChange={(e) => handleChange(loc.id, e)}
                          removeBackGround
                        />
                      </div>
                      <FieldError error={errors[loc.id]?.capacity} />
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        ))}
        <Stack direction="row" justifyContent="space-between" className={styles["modalFooterStack"]}>
          <span 
            onClick={handleAddLocation}
            className={styles["addAnotherLocationText"]}
          >
            + Add Another Location
          </span>
          <Stack direction="row" spacing={1.5}>
            <CommonButton
              label="Cancel"
              onClick={onClose}
              disabled={isSubmitting}
              removeBorder
              fitContent
              className={styles["smallBtn"]}
              fontSize={"12px"}
            />
            <CommonButton
              label={isSubmitting ? "Adding..." : "Save"}
              onClick={handleSave}
              disabled={isSubmitting}
              filledButton
              fitContent
              className={styles["smallBtn"]}
              fontSize={"12px"}
            />
          </Stack>
        </Stack>

      </Box>
    </CommonModal>
  );
};

export default AddLocationModal;